import { useMemo, useState } from 'react';
import { useProdutos } from '@/features/produtos/hooks/useProdutos';
import { ProdutoFormModal } from '@/features/produtos/pages/ProdutoFormModal';
import type { ProdutoResumo } from '@/features/produtos/api/produtosApi';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Paginacao } from '@/shared/ui/Paginacao';
import { SearchInput, normalizarBusca } from '@/shared/ui/SearchInput';
import { IconBox, IconEdit, IconRefresh, IconTrash } from '@/shared/ui/Icon';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import { useRotaEntidade } from '@/app/useRotaEntidade';
import { ROTAS } from '@/app/rotas';

const formatadorMoeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

function formatarPreco(valor: number) {
  return formatadorMoeda.format(valor);
}

type ModoForm =
  | { tipo: 'fechado' }
  | { tipo: 'novo' }
  | { tipo: 'editar'; produto: ProdutoResumo };

export function ProdutosPage() {
  const { produtos, carregando, erro, recarregar, atualizar, excluir } = useProdutos();
  // Criar e editar têm endereço próprio (`/produtos/novo`,
  // `/produtos/:id/editar`).
  const rota = useRotaEntidade(ROTAS.produtos);
  const [busca, setBusca] = useState('');
  const dialogos = useDialogos();

  const modo = useMemo<ModoForm>(() => {
    if (rota.modo === 'novo') return { tipo: 'novo' };
    if (rota.modo === 'editar') {
      const registro = produtos.find((x) => x.id === rota.id);
      return registro ? { tipo: 'editar', produto: registro } : { tipo: 'fechado' };
    }
    return { tipo: 'fechado' };
  }, [rota, produtos]);

  const filtrados = useMemo(() => {
    const q = normalizarBusca(busca.trim());
    if (q === '') return produtos;
    return produtos.filter((p) =>
      normalizarBusca(`${p.codigo} ${p.nome}`).includes(q),
    );
  }, [produtos, busca]);

  const { pagina, setPagina, paginados, total, totalPaginas, tamanhoPagina } =
    usePaginacao(filtrados, 25);

  async function confirmarExclusao(produto: ProdutoResumo) {
    const ok = await dialogos.confirmar({
      titulo: 'Excluir produto',
      mensagem: `Excluir o produto "${produto.nome}"?`,
      textoConfirmar: 'Excluir',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await excluir(produto.id);
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao excluir.');
    }
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Produtos"
        descricao="Itens vendáveis disponíveis para compor ordens de serviço."
        acoes={
          <>
            <Button onClick={() => void recarregar()} disabled={carregando}>
              <IconRefresh className="btn__icon" />
              {carregando ? 'Atualizando…' : 'Atualizar'}
            </Button>
            <Button variante="primary" onClick={() => rota.abrir('novo')}>
              + Novo produto
            </Button>
          </>
        }
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          Falha ao carregar: {erro}
        </p>
      )}

      <div className="card">
        <div className="card__header card__header--com-busca">
          <span className="card__title">
            {filtrados.length} de {produtos.length} produto(s)
          </span>
          <SearchInput
            valor={busca}
            onChange={setBusca}
            placeholder="Buscar por código ou nome…"
            ariaLabel="Buscar produto"
          />
        </div>
        <div className="card__body card__body--flush">
          {produtos.length === 0 && !carregando && !erro ? (
            <EmptyState
              icone={<IconBox />}
              titulo="Nenhum produto cadastrado ainda."
              descricao="Cadastre produtos para utilizá-los nas ordens de serviço."
            />
          ) : filtrados.length === 0 ? (
            <EmptyState
              icone={<IconBox />}
              titulo="Nenhum produto corresponde à busca."
              descricao="Tente outro termo ou limpe o campo de busca."
            />
          ) : (
            <div className="table-scroll"><table className="table table--responsive">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th className="table__numeric">Compra</th>
                  <th className="table__numeric">Margem</th>
                  <th className="table__numeric">Venda</th>
                  <th className="table__numeric">Estoque</th>
                  <th>Status</th>
                  <th style={{ width: 1 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map((p) => (
                  <tr key={p.id}>
                    <td className="table__primary">{p.codigo}</td>
                    <td data-label="Nome">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {p.nome}
                        <BadgeSyncPendente pendente={p.syncPendente} />
                      </span>
                    </td>
                    <td className="table__numeric" data-label="Compra">{formatarPreco(p.precoCompra ?? 0)}</td>
                    <td className="table__numeric" data-label="Margem">{(p.margemPercentual ?? 0).toFixed(2)}%</td>
                    <td className="table__numeric" data-label="Venda">{formatarPreco(p.precoUnitario ?? 0)}</td>
                    <td className="table__numeric" data-label="Estoque">{Math.max(p.estoqueQuantidade ?? 0, 0).toLocaleString('pt-BR')}</td>
                    <td data-label="Status">
                      {p.ativo ? (
                        <span className="badge badge--ativo">Ativo</span>
                      ) : (
                        <span className="badge badge--inativo">(inativo)</span>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        <Button
                          className="btn--icon"
                          aria-label={`Editar ${p.nome}`}
                          onClick={() => rota.abrir(`${p.id}/editar`)}
                        >
                          <IconEdit />
                        </Button>
                        <Button
                          className="btn--icon btn--danger"
                          aria-label={`Excluir ${p.nome}`}
                          onClick={() => void confirmarExclusao(p)}
                        >
                          <IconTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          <Paginacao
            pagina={pagina}
            totalPaginas={totalPaginas}
            total={total}
            tamanhoPagina={tamanhoPagina}
            onMudarPagina={setPagina}
          />
        </div>
      </div>

      {modo.tipo !== 'fechado' && (
        <ProdutoFormModal
          modo={modo}
          aoFechar={rota.fechar}
          aoSalvar={async (dados) => {
            if (modo.tipo === 'editar') {
              await atualizar(modo.produto.id, dados);
            }
            rota.fechar();
            await recarregar();
          }}
        />
      )}
    </section>
  );
}
